import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, User, Phone, MapPin } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

const EMPTY_ADDR = { line1: "", line2: "", city: "", state: "", postal_code: "", country: "" };

export default function MyAccount() {
  const { refreshUser } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setForm({
        name: r.data.name || "",
        phone: r.data.phone || "",
        email: r.data.email,
        shipping_address: { ...EMPTY_ADDR, ...(r.data.shipping_address || {}) },
        billing_address: { ...EMPTY_ADDR, ...(r.data.billing_address || {}) },
      });
    }).catch(() => toast.error("Failed to load profile"));
  }, []);

  const setAddr = (which, key, val) => {
    setForm((f) => ({ ...f, [which]: { ...f[which], [key]: val } }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    try {
      const payload = { name: form.name, phone: form.phone, shipping_address: form.shipping_address, billing_address: form.billing_address };
      await api.patch("/auth/me", payload);
      toast.success("Profile saved");
      refreshUser?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <div className="rgu-container py-16 max-w-4xl">
        <div className="rgu-eyebrow text-slate-500 mb-3">Account · Profile</div>
        <h1 className="font-display text-3xl md:text-4xl text-[#0A192F]">My Account</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          Keep your contact and shipping details current so we can reach you about your authentications and ship completed orders back to the right address.
        </p>

        {!form ? (
          <div className="py-12 text-center text-slate-500 font-mono-rgu text-sm">Loading…</div>
        ) : (
          <form onSubmit={save} className="mt-10 space-y-10" data-testid="account-form">
            <section>
              <div className="rgu-eyebrow text-slate-500 text-[10px] mb-4 flex items-center gap-2"><User className="w-3 h-3" /> Contact</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="acct-name">Name</Label>
                  <Input id="acct-name" data-testid="acct-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-none" />
                </div>
                <div>
                  <Label htmlFor="acct-email">Email</Label>
                  <Input id="acct-email" value={form.email} disabled className="mt-2 rounded-none bg-slate-50" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="acct-phone"><Phone className="w-3 h-3 inline mr-1" /> Phone</Label>
                  <Input id="acct-phone" data-testid="acct-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 rounded-none" placeholder="+1 555 123 4567" />
                </div>
              </div>
            </section>

            <section>
              <div className="rgu-eyebrow text-slate-500 text-[10px] mb-4 flex items-center gap-2"><MapPin className="w-3 h-3" /> Shipping Address</div>
              <AcctAddressFields prefix="acct-ship" addr={form.shipping_address} onChange={(k, v) => setAddr("shipping_address", k, v)} />
            </section>

            <section>
              <div className="rgu-eyebrow text-slate-500 text-[10px] mb-4 flex items-center justify-between">
                <span className="flex items-center gap-2"><MapPin className="w-3 h-3" /> Billing Address</span>
                <button type="button" className="text-[10px] text-[#0A192F] underline normal-case tracking-normal" onClick={() => setForm({ ...form, billing_address: { ...form.shipping_address } })} data-testid="acct-copy-shipping">Copy from Shipping</button>
              </div>
              <AcctAddressFields prefix="acct-bill" addr={form.billing_address} onChange={(k, v) => setAddr("billing_address", k, v)} />
            </section>

            <div className="flex justify-end">
              <button type="submit" disabled={saving} className="rgu-btn-primary px-6 text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="acct-save">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}

function AcctAddressFields({ prefix, addr, onChange }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
      <div className="md:col-span-6"><Label htmlFor={`${prefix}-line1`}>Address Line 1</Label><Input id={`${prefix}-line1`} data-testid={`${prefix}-line1`} value={addr.line1 || ""} onChange={(e) => onChange("line1", e.target.value)} className="mt-1.5 rounded-none" /></div>
      <div className="md:col-span-6"><Label htmlFor={`${prefix}-line2`}>Address Line 2 <span className="text-slate-400">(optional)</span></Label><Input id={`${prefix}-line2`} data-testid={`${prefix}-line2`} value={addr.line2 || ""} onChange={(e) => onChange("line2", e.target.value)} className="mt-1.5 rounded-none" /></div>
      <div className="md:col-span-3"><Label htmlFor={`${prefix}-city`}>City</Label><Input id={`${prefix}-city`} data-testid={`${prefix}-city`} value={addr.city || ""} onChange={(e) => onChange("city", e.target.value)} className="mt-1.5 rounded-none" /></div>
      <div className="md:col-span-1"><Label htmlFor={`${prefix}-state`}>State</Label><Input id={`${prefix}-state`} data-testid={`${prefix}-state`} value={addr.state || ""} onChange={(e) => onChange("state", e.target.value)} className="mt-1.5 rounded-none" /></div>
      <div className="md:col-span-1"><Label htmlFor={`${prefix}-zip`}>ZIP</Label><Input id={`${prefix}-zip`} data-testid={`${prefix}-zip`} value={addr.postal_code || ""} onChange={(e) => onChange("postal_code", e.target.value)} className="mt-1.5 rounded-none" /></div>
      <div className="md:col-span-1"><Label htmlFor={`${prefix}-country`}>Country</Label><Input id={`${prefix}-country`} data-testid={`${prefix}-country`} value={addr.country || ""} onChange={(e) => onChange("country", e.target.value)} className="mt-1.5 rounded-none" /></div>
    </div>
  );
}
